import { Routes } from '@angular/router';
import { authGuard, guestGuard } from './core/auth/guards/auth.guard';
import { permissionGuard } from './core/auth/guards/permission.guard';
import { adminRoleGuard } from './core/auth/guards/admin-role.guard';
import { planFeatureChildGuard } from './core/auth/guards/plan-feature.guard';

const ACCESS = {
  pos: ['caisse:write'],
  salesHistory: ['vente:read', 'vente:write'],
  invoices: ['facture:read', 'facture:write'],
  invoiceWrite: ['facture:write'],
  catalogue: ['stock:read', 'stock:write', 'catalogue:read'],
  stockRead: ['stock:read', 'stock:write'],
  stockWrite: ['stock:write', 'catalogue:write'],
  inventoryRead: ['inventaire:read'],
  inventoryWrite: ['inventaire:write'],
  clients: ['client:read', 'client:write', 'commande:write', 'facture:write'],
  purchases: ['fournisseur:read', 'commande_achat:write'],
  purchaseWrite: ['commande_achat:write'],
  expenses: ['commande_achat:write', 'rapport:read'],
  reports: ['rapport:read'],
  accounting: ['comptabilite:read'],
  accountingWrite: ['comptabilite:write'],
  hr: ['rh:read'],
  hrWrite: ['rh:write'],
  users: ['utilisateur:write'],
  renewal: ['abonnement:renew'],
  admin: ['*'],
};

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () =>
      import('./features/public/home/home.component').then((module) => module.HomeComponent),
  },
  {
    path: 'documents/:slug',
    loadComponent: () =>
      import('./features/public/legal-content.component').then(
        (module) => module.LegalContentComponent,
      ),
  },
  {
    path: 'portail-client/:token',
    loadComponent: () =>
      import('./features/public/client-portal/client-portal.component').then(
        (module) => module.ClientPortalComponent,
      ),
  },
  {
    path: 'connexion',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./features/auth/login/login.component').then((module) => module.LoginComponent),
  },
  {
    path: 'mot-de-passe-oublie',
    loadComponent: () =>
      import('./features/auth/forgot-password/forgot-password.component').then(
        (module) => module.ForgotPasswordComponent,
      ),
  },
  {
    path: 'reinitialiser-mot-de-passe',
    loadComponent: () =>
      import('./features/auth/reset-password/reset-password.component').then(
        (module) => module.ResetPasswordComponent,
      ),
  },
  {
    path: 'inscription',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./features/auth/register/register.component').then(
        (module) => module.RegisterComponent,
      ),
  },
  {
    path: 'inscription/paiement/succes',
    data: { mode: 'success' },
    loadComponent: () =>
      import('./features/auth/payment-result/payment-result.component').then(
        (module) => module.PaymentResultComponent,
      ),
  },
  {
    path: 'inscription/paiement/annule',
    data: { mode: 'cancel' },
    loadComponent: () =>
      import('./features/auth/payment-result/payment-result.component').then(
        (module) => module.PaymentResultComponent,
      ),
  },
  {
    path: '',
    canActivate: [authGuard],
    canActivateChild: [planFeatureChildGuard],
    loadComponent: () =>
      import('./layout/shell/shell.component').then((module) => module.ShellComponent),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'tableau-de-bord' },
      {
        path: 'tableau-de-bord',
        data: { requiredFeature: 'dashboard.basic' },
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then(
            (module) => module.DashboardComponent,
          ),
      },

      {
        path: 'notifications',
        data: { requiredFeature: 'notifications.basic' },
        loadComponent: () =>
          import('./features/notifications/notifications-center.component').then(
            (module) => module.NotificationsCenterComponent,
          ),
      },
      {
        path: 'synchronisation',
        data: { requiredFeature: 'offline.sync' },
        loadComponent: () =>
          import('./features/offline/synchronisation/synchronisation.component').then(
            (module) => module.SynchronisationComponent,
          ),
      },

      // Vente & Caisse
      {
        path: 'vente-caisse/pos',
        canActivate: [permissionGuard],
        data: { hideNewSaleButton: true, permissions: ACCESS.pos },
        loadComponent: () =>
          import('./features/vente-caisse/pos/pos.component').then(
            (module) => module.PosComponent,
          ),
      },
      {
        path: 'vente-caisse/sessions',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.pos },
        loadComponent: () =>
          import('./features/vente-caisse/sessions/caisse-sessions.component').then(
            (module) => module.CaisseSessionsComponent,
          ),
      },
      {
        path: 'vente-caisse/caisses',
        redirectTo: 'vente-caisse/sessions',
        pathMatch: 'full',
      },
      {
        path: 'vente-caisse/historique',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.salesHistory },
        loadComponent: () =>
          import('./features/vente-caisse/historique/ventes-history.component').then(
            (module) => module.VentesHistoryComponent,
          ),
      },

      // Facturation
      {
        path: 'facturation',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.invoices },
        loadComponent: () =>
          import('./features/facturation/liste/facturation-list.component').then(
            (module) => module.FacturationListComponent,
          ),
      },
      {
        path: 'facturation/recurrente',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.invoiceWrite, requiredFeature: 'billing.recurring' },
        loadComponent: () =>
          import('./features/facturation/recurrente/facturation-recurrente.component').then(
            (module) => module.FacturationRecurrenteComponent,
          ),
      },
      {
        path: 'facturation/nouvelle',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.invoiceWrite },
        loadComponent: () =>
          import('./features/facturation/nouveau/facture-form.component').then(
            (module) => module.FactureFormComponent,
          ),
      },

      // Catalogue & Stocks
      {
        path: 'catalogue-stocks/produits',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.catalogue },
        loadComponent: () =>
          import('./features/catalogue-stocks/produits/produits-list.component').then(
            (module) => module.ProduitsListComponent,
          ),
      },
      {
        path: 'catalogue-stocks/produits/nouveau',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.stockWrite },
        loadComponent: () =>
          import('./features/catalogue-stocks/produits/produit-form.component').then(
            (module) => module.ProduitFormComponent,
          ),
      },
      {
        path: 'catalogue-stocks/produits/:id/modifier',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.stockWrite },
        loadComponent: () =>
          import('./features/catalogue-stocks/produits/produit-form.component').then(
            (module) => module.ProduitFormComponent,
          ),
      },
      {
        path: 'catalogue-stocks/produits/:id',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.catalogue },
        loadComponent: () =>
          import('./features/catalogue-stocks/produits/produit-form.component').then(
            (module) => module.ProduitFormComponent,
          ),
      },
      {
        path: 'catalogue-stocks/categories',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.catalogue },
        loadComponent: () =>
          import('./features/catalogue-stocks/categories/categories.component').then(
            (module) => module.CategoriesComponent,
          ),
      },
      {
        path: 'catalogue-stocks/unites',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.catalogue },
        loadComponent: () =>
          import('./features/catalogue-stocks/unites/unites.component').then(
            (module) => module.UnitesComponent,
          ),
      },
      {
        path: 'catalogue-stocks/inventaire',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.inventoryRead, requiredFeature: 'stock.inventory' },
        loadComponent: () =>
          import('./features/catalogue-stocks/inventaire/inventaire.component').then(
            (module) => module.InventaireComponent,
          ),
      },
      {
        path: 'catalogue-stocks/tracabilite',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.stockRead, requiredFeature: 'stock.lot_serial' },
        loadComponent: () =>
          import('./features/catalogue-stocks/tracabilite/stock-tracabilite.component').then(
            (module) => module.StockTracabiliteComponent,
          ),
      },
      {
        path: 'catalogue-stocks/mouvements',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.stockRead },
        loadComponent: () =>
          import('./features/catalogue-stocks/mouvements/mouvements.component').then(
            (module) => module.MouvementsComponent,
          ),
      },
      {
        path: 'catalogue-stocks/entrepots',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.stockRead },
        loadComponent: () =>
          import('./features/catalogue-stocks/entrepots/entrepots.component').then(
            (module) => module.EntrepotsComponent,
          ),
      },

      // Clients
      {
        path: 'clients',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.clients },
        loadComponent: () =>
          import('./features/clients/liste/clients-list.component').then(
            (module) => module.ClientsListComponent,
          ),
      },
      {
        path: 'clients/nouveau',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.clients },
        loadComponent: () =>
          import('./features/clients/detail/client-form.component').then(
            (module) => module.ClientFormComponent,
          ),
      },
      {
        path: 'clients/:id/modifier',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.clients },
        loadComponent: () =>
          import('./features/clients/detail/client-form.component').then(
            (module) => module.ClientFormComponent,
          ),
      },
      {
        path: 'crm',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.clients, requiredFeature: 'customers.crm' },
        loadComponent: () =>
          import('./features/crm/crm.component').then((module) => module.CrmComponent),
      },
      {
        path: 'fidelite',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.clients, requiredFeature: 'customers.loyalty' },
        loadComponent: () =>
          import('./features/fidelite/fidelite.component').then((module) => module.FideliteComponent),
      },
      {
        path: 'workflows',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.purchases, requiredFeature: 'workflow.approval' },
        loadComponent: () =>
          import('./features/workflows/workflows.component').then((module) => module.WorkflowsComponent),
      },
      {
        path: 'clients/:id/dossier',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.clients, type: 'client' },
        loadComponent: () =>
          import('./features/tiers/dossier/tiers-dossier.component').then(
            (module) => module.TiersDossierComponent,
          ),
      },

      // Achats & Dépenses
      {
        path: 'achats-depenses/scanner',
        canActivate: [permissionGuard],
        data: { permissions: ['fournisseur:read', 'commande_achat:write'], requiredFeature: 'purchases.document_scan' },
        loadComponent: () =>
          import('./features/achats-depenses/documents-scannes/documents-scannes.component').then(
            (m) => m.DocumentsScannesComponent,
          ),
      },
      {
        path: 'achats-depenses/fournisseurs',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.purchases },
        loadComponent: () =>
          import('./features/achats-depenses/fournisseurs/fournisseurs.component').then(
            (module) => module.FournisseursComponent,
          ),
      },
      {
        path: 'achats-depenses/fournisseurs/:id/dossier',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.purchases, type: 'fournisseur' },
        loadComponent: () =>
          import('./features/tiers/dossier/tiers-dossier.component').then(
            (module) => module.TiersDossierComponent,
          ),
      },
      {
        path: 'achats-depenses/bons-commande',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.purchases, requiredFeature: 'purchases.orders' },
        loadComponent: () =>
          import('./features/achats-depenses/commandes/commandes-achats.component').then(
            (module) => module.CommandesAchatsComponent,
          ),
      },
      {
        path: 'achats-depenses/receptions',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.purchaseWrite, requiredFeature: 'purchases.orders' },
        loadComponent: () =>
          import('./features/achats-depenses/receptions/receptions.component').then(
            (module) => module.ReceptionsComponent,
          ),
      },
      {
        path: 'achats-depenses/depenses',
        canActivate: [permissionGuard],
        data: { mode: 'depense', permissions: ACCESS.expenses },
        loadComponent: () =>
          import('./features/achats-depenses/depenses/depenses.component').then(
            (module) => module.DepensesComponent,
          ),
      },
      {
        path: 'achats-depenses/credits-avances',
        canActivate: [permissionGuard],
        data: { mode: 'credit', permissions: ACCESS.expenses },
        loadComponent: () =>
          import('./features/achats-depenses/depenses/depenses.component').then(
            (module) => module.DepensesComponent,
          ),
      },

      {
        path: 'rapports',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.reports, requiredFeature: 'reports.basic' },
        loadComponent: () =>
          import('./features/reporting/reporting.component').then((module) => module.ReportingComponent),
      },

      // Comptabilité
      {
        path: 'comptabilite/plan-comptable',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.accounting, requiredFeature: 'accounting.basic' },
        loadComponent: () =>
          import('./features/comptabilite/plan-comptable/plan-comptable.component').then(
            (module) => module.PlanComptableComponent,
          ),
      },
      {
        path: 'comptabilite/journal',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.accounting, requiredFeature: 'accounting.basic' },
        loadComponent: () =>
          import('./features/comptabilite/journal/journal.component').then(
            (module) => module.JournalComponent,
          ),
      },
      {
        path: 'comptabilite/rapports',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.accounting, requiredFeature: 'accounting.basic' },
        loadComponent: () =>
          import('./features/comptabilite/rapports/rapports.component').then(
            (module) => module.RapportsComponent,
          ),
      },
      {
        path: 'comptabilite/tresorerie',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.accounting, requiredFeature: 'accounting.basic' },
        loadComponent: () =>
          import('./features/comptabilite/tresorerie/tresorerie.component').then(
            (module) => module.TresorerieComponent,
          ),
      },

      {
        path: 'comptabilite/syscohada',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.accounting, requiredFeature: 'accounting.syscohada' },
        loadComponent: () =>
          import('./features/comptabilite/syscohada/syscohada.component').then(
            (module) => module.SyscohadaComponent,
          ),
      },

      {
        path: 'comptabilite/analytique',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.accounting, requiredFeature: 'accounting.advanced' },
        loadComponent: () =>
          import('./features/comptabilite/analytique/comptabilite-analytique.component').then(
            (module) => module.ComptabiliteAnalytiqueComponent,
          ),
      },

      // Compte personnel
      {
        path: 'mon-compte/securite',
        loadComponent: () =>
          import('./features/compte/securite/compte-securite.component').then(
            (module) => module.CompteSecuriteComponent,
          ),
      },

      // Paramètres
      {
        path: 'parametres/organisation',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.admin },
        loadComponent: () =>
          import('./features/parametres/organisation/organisation.component').then(
            (module) => module.OrganisationSettingsComponent,
          ),
      },
      {
        path: 'parametres/utilisateurs',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.users },
        loadComponent: () =>
          import('./features/parametres/utilisateurs/utilisateurs.component').then(
            (module) => module.UtilisateursComponent,
          ),
      },
      {
        path: 'parametres/ventes-taxes',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.admin },
        loadComponent: () =>
          import('./features/parametres/ventes-taxes/ventes-taxes.component').then(
            (module) => module.VentesTaxesComponent,
          ),
      },
      { path: 'parametres/integrations', redirectTo: 'parametres/organisation', pathMatch: 'full' },
      {
        path: 'parametres/abonnement',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.renewal },
        loadComponent: () =>
          import('./features/parametres/abonnement/abonnement.component').then(
            (module) => module.AbonnementComponent,
          ),
      },
      {
        path: 'parametres/documents',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.admin },
        loadComponent: () =>
          import('./features/parametres/documents/documents.component').then(
            (module) => module.DocumentsAdherentComponent,
          ),
      },
      { path: 'parametres/modeles-documents', redirectTo: 'parametres/organisation', pathMatch: 'full' },
      {
        path: 'parametres/regles-alertes',
        canActivate: [permissionGuard],
        data: { permissions: ACCESS.admin, requiredFeature: 'notifications.advanced' },
        loadComponent: () =>
          import('./features/parametres/regles-alertes/regles-alertes.component').then(
            (module) => module.ReglesAlertesComponent,
          ),
      },
      {
        path: 'parametres/historique',
        canActivate: [permissionGuard, adminRoleGuard],
        data: { permissions: ACCESS.admin, requiredFeature: 'audit.full' },
        loadComponent: () =>
          import('./features/parametres/historique/historique.component').then(
            (module) => module.HistoriqueComponent,
          ),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
